import { useContext, useEffect, useState } from "react";
import * as FC from "@fluentui/react-components";
import { apiClient } from "./api";
import { TableDef } from "../table_def";
import { toDateTimeString } from "../date_utils";
import { Context } from "../context";

const resolveClient = (client) => {
    if (client && typeof client.get === "function") {
        return client;
    }

    return apiClient;
};

/**
 * List customers with optional search filter.
 * @param {*} _client - optional client instance (apiClient by default)
 * @param {string|null} filter
 * @param {{ includeDisabled?: boolean, tenantId?: number|string, assetOwnerId?: number|string }} options
 * @returns {Promise<Array>}
 */
export const listCustomer = async (_client, filter, options = {}) => {
    try {
        const http = resolveClient(_client);
        if (!http) {
            return [];
        }

        const params = {
            includeDisabled: options.includeDisabled ?? true,
        };

        if (filter && filter.trim()) {
            params.search = filter.trim();
        }

        if (options.tenantId) {
            params.tenantId = options.tenantId;
        }

        if (options.assetOwnerId) {
            params.assetOwnerId = options.assetOwnerId;
        }

        const response = await http.get("/customer", { params });
        return response || [];
    } catch (error) {
        console.error("Failed to list customers:", error);
        return [];
    }
};

export const CustomerDef = new TableDef("customer", {
    customerCode: {
        label: "客户编号",
        display: (item) => item.customerCode || item.customer_code,
    },
    companyName: {
        label: "公司名称",
        display: (item) => item.companyName || item.company_name,
    },
    tenantName: {
        label: "Tenant",
        display: (item) => item.tenantName || item.tenant_name || "-",
    },
    assetOwnerName: {
        label: "Asset Owner",
        display: (item) => item.assetOwnerName || item.asset_owner_name || "-",
    },
    name: {
        label: "联系人",
    },
    contactEmail: {
        label: "邮箱",
    },
    contactPhone: {
        label: "联系电话",
    },
    disabled: {
        label: "状态",
        display: (item) => (item.disabled ? "禁用" : "正常"),
    },
    updatedAt: {
        label: "更新时间",
        display: (item) =>
            toDateTimeString(item.updatedAt || item.updated_at),
    },
});

/**
 * Read helpers for customer accounts.
 *
 * The write helpers that used to live here are gone on purpose. Customer writes are tenant-scoped
 * now — `apiClient.createManagedCustomer` / `updateManagedCustomer` / `disableManagedCustomer` /
 * `enableManagedCustomer`, all of which take the tenant as their first argument. The old ones took
 * no tenant at all, and one of them (`enable`) worked by sending `PUT { disabled: false }`, which
 * the API now refuses with 400 `ImmutableField` because enable/disable are their own actions.
 *
 * Leaving them here would have been a trap: a helper that still looks callable, still type-checks,
 * and fails only at runtime against real data.
 */
export const customerService = {
    list: (params = {}, client) =>
        resolveClient(client).get("/customer", {
            params: { includeDisabled: true, ...params },
        }),
    get: (id, client) => resolveClient(client).get(`/customer/${id}`),
    sessions: (id, client) =>
        resolveClient(client).get(`/customer/${id}/sessions`),
};

export const CustomerDropdown = ({
    value,
    onChange,
    disabled = false,
    placeholder = "请选择客户",
    includeDisabled = false,
    tenantId = null,
    assetOwnerId = null,
}) => {
    const { apiClient } = useContext(Context);
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        const loadCustomers = async () => {
            try {
                const response = await listCustomer(
                    apiClient,
                    null,
                    { includeDisabled, tenantId, assetOwnerId }
                );
                setCustomers(response || []);
            } catch (error) {
                console.error("Failed to load customers:", error);
                setCustomers([]);
            }
        };

        loadCustomers();
    }, [apiClient, includeDisabled, tenantId, assetOwnerId]);

    const selectedOptions = value ? [value.toString()] : [];

    return (
        <FC.Dropdown
            size="small"
            disabled={disabled}
            value={selectedOptions}
            placeholder={placeholder}
            onOptionSelect={(_, data) => {
                if (onChange) {
                    onChange(data.optionValue);
                }
            }}
        >
            {customers
                .filter((customer) => includeDisabled || !customer.disabled)
                .map((customer) => (
                    <FC.Option
                        key={customer.id}
                        value={customer.id.toString()}
                    >
                        {`${customer.companyName || customer.company_name} (${customer.customerCode || customer.customer_code})`}
                    </FC.Option>
                ))}
        </FC.Dropdown>
    );
};

export default customerService;
